import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import NavIcon from "../components/NavIcon";
import { ACTIVE_PAGES, DIVISIONS } from "../constants/pageMaster";
import { candidatesAPI, offersAPI } from "../api/client";
import {
  canAccessNavItem,
  canAccessRouteByUser,
  mayPerform,
} from "../utils/userAccess";
import { getRecentlyUsed } from "../utils/recentlyUsed";
import { isPrivilegedRole } from "../utils/accessControl";
import "./Home.css";

// Presentation-only alias: the shared Page Master stores an `icon` token per
// page, but a couple of tokens (chat, star) have no dedicated SVG in the
// existing icon set. Map them to the same existing icon types the sidebar
// already uses for those pages so every tile reuses an existing portal icon.
const ICON_ALIASES = {
  chat: "calendar",
  star: "sparkles",
};

// Home-level modules (e.g. Support Tickets) live in their own "Home / Global"
// section rather than inside a team. Reference the Page Master division name so
// the section derives from real page config, never a duplicated list here.
const HOME_DIVISION = "Home / Global";

// Presentation-only accent tokens per team (keyed by the Page Master division
// name). Purely decorative UI color — no RBAC or config involvement; any team
// without a token falls back to the portal blue.
const DIVISION_ACCENT = {
  "TA Team": { main: "#2563eb", soft: "#e8f0fe", border: "#bcd3fb" },
  "HR Team": { main: "#0d9488", soft: "#e4f7f4", border: "#b5e6df" },
  "Finance Team": { main: "#7c3aed", soft: "#f0eafd", border: "#d9c9f6" },
  "Sales Team": { main: "#db2777", soft: "#fde8f1", border: "#f5c3d8" },
  "Admin Team": { main: "#d97706", soft: "#fdf2e3", border: "#f3d9ae" },
  [HOME_DIVISION]: { main: "#0f766e", soft: "#e6f4f4", border: "#c5e5e3" },
};
const DEFAULT_ACCENT = { main: "#2f67ff", soft: "#eef4ff", border: "#d9e6fb" };

// Accent token used per My Action card (presentation only, keyed by action id).
const ACTION_ACCENT = {
  "pending-reviews": { main: "#d97706", soft: "#fdf2e3", border: "#f3d9ae" },
  "pending-tasks": { main: "#0d9488", soft: "#e4f7f4", border: "#b5e6df" },
};

function accentVars(accent) {
  return {
    "--acc": accent.main,
    "--acc-soft": accent.soft,
    "--acc-border": accent.border,
  };
}

// The Page Master can contain config-only entries that are actually surfaced
// inside another page (e.g. My Performance is a tab within Profile, Analytics
// is a tab inside Sudo Access), not as their own route. Never render a link to
// a page the portal does not route to.
const UNROUTED_PAGE_KEYS = new Set([
  "my_performance",
  "reimbursement_dashboard",
  "reimbursement_approved",
  "analytics",
]);
const RECENT_LIMIT = 5;
const TEAM_DESC_MAX = 3;

// Landing page per team when a team card is clicked on Home. Uses existing
// page routes only (with optional query strings to preselect a tab). Falls
// back to the first accessible module if the team's lead route is not part of
// the user's access snapshot.
const TEAM_LEAD_ROUTE = {
  "TA Team": "/dashboard",
  "HR Team": "/headcount",
  "Finance Team": "/project-mis?tab=revenue",
  "Sales Team": "/sales",
  "Admin Team": "/masters",
};

// Build the nav-item shape the shared access gate expects from a Page Master
// entry: the route plus the legacy VIEW permission for the pre-snapshot
// fallback (global defaults and the access snapshot are still resolved first
// by canAccessNavItem).
function toNavItem(page) {
  const view = (page.actions || []).find((a) => a.action === "view");
  return {
    to: page.route,
    permission: view && view.permission ? view.permission : null,
  };
}

// Same date format the top bar uses, so the Welcome header stays consistent
// with the rest of the portal.
function formatDate() {
  return new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

// Compact relative label for the Recently Used chips.
function formatWhen(timestamp) {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days <= 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
}

export default function Home({ user, userRole = "HR" }) {
  const role = userRole;
  const name = (user && (user.name || user.username)) || "";

  // Derived from the SAME access snapshot, matrix and legacy permission rules
  // used by the Sidebar (via the shared canAccessNavItem / canAccessRouteByUser
  // helpers). Recompute on every render: the access snapshot loads
  // asynchronously after login and App re-renders Home once it resolves.
  const accessiblePages = ACTIVE_PAGES.filter(
    (p) =>
      !UNROUTED_PAGE_KEYS.has(p.pageKey) &&
      (p.pageKey !== "master" || isPrivilegedRole(role)) &&
      canAccessNavItem(role, toNavItem(p)) &&
      canAccessRouteByUser(role, p.route)
  );

  // "My Actions" counts come from existing portal endpoints only — never
  // invented or mocked. Each card is skipped when the user cannot open the
  // destination route or the endpoint is unavailable for the account.
  const [actions, setActions] = useState([]);
  const [actionsReady, setActionsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadActions = async () => {
      const found = [];

      // Pending offer approvals awaiting this user (existing /offers/my/pending
      // — includes delegated-to-me approvals).
      if (canAccessRouteByUser(role, "/offers")) {
        try {
          const res = await offersAPI.getMyPending();
          const offers = res?.data?.offers;
          const count =
            Number(res?.data?.count) ||
            (Array.isArray(offers) ? offers.length : 0);
          found.push({
            id: "pending-reviews",
            label: "Pending Reviews",
            note: "Offers awaiting your approval",
            route: "/offers",
            icon: "file",
            count,
          });
        } catch {
          // Not permitted / endpoint unavailable — leave the card out rather
          // than inventing a count.
        }
      }

      // Candidates currently awaiting screening (existing /candidates
      // status filter, count surfaced by the same pagination the list uses).
      if (canAccessRouteByUser(role, "/candidates")) {
        try {
          const res = await candidatesAPI.getAll({
            status: "screen_pending",
            mine: true,
            page: 1,
            pageSize: 1,
          });
          found.push({
            id: "pending-tasks",
            label: "Pending Tasks",
            note: "Your candidates awaiting screening",
            route: "/candidates?status=screen_pending&mine=true",
            icon: "people",
            count: Number(res?.data?.pagination?.total) || 0,
          });
        } catch {
          // Not permitted / endpoint unavailable — leave the card out.
        }
      }

      if (!cancelled) {
        setActions(found);
        setActionsReady(true);
      }
    };

    loadActions();
    return () => {
      cancelled = true;
    };
  }, [role]);

  // "Recently Used": history recorded on navigation, re-filtered through the
  // same RBAC gates so a chip never points at a page the user can no longer
  // open. Read on mount — Home remounts after every module visit, always with
  // the freshest history.
  const [recentUsed] = useState(() => getRecentlyUsed());

  const pageByRoute = new Map(accessiblePages.map((p) => [p.route, p]));
  const recentPages = recentUsed
    .map((entry) => {
      const page = pageByRoute.get(entry.route);
      return page ? { ...entry, page } : null;
    })
    .filter(Boolean)
    .slice(0, RECENT_LIMIT);

  // Teams the user is actually allocated / access-enabled for: a team card is
  // shown only when the user can open at least one of its pages under the SAME
  // shared RBAC resolution used by the Sidebar and the route guards. Clicking
  // a team opens its workspace (lead page), which scopes the Sidebar to that
  // team. Derivation uses the Page Master DIVISIONS order — no new config.
  // Home-level modules (division "Home / Global", e.g. Support Tickets) are NOT
  // teams: they are separated out below and rendered in their own section.
  const teams = [];
  const homeModules = [];
  for (const div of DIVISIONS) {
    const pages = accessiblePages
      .filter((p) => p.division === div)
      .slice()
      .sort((a, b) => a.displayOrder - b.displayOrder);
    if (pages.length === 0) continue;
    if (div === HOME_DIVISION) {
      homeModules.push(...pages);
      continue;
    }
    const pref = TEAM_LEAD_ROUTE[div];
    const prefBase = pref ? pref.split("?")[0] : null;
    const preferredPage = prefBase ? pages.find((p) => p.route === prefBase) : null;
    const lead = preferredPage || pages[0];
    const leadTo = preferredPage ? pref : lead.route;
    teams.push({ division: div, pages, lead, leadTo });
  }

  // Short per-team description derived from the SAME page configuration used
  // by the Sidebar (no new team config / content): a compact list of the first
  // module labels, elided when the team has more modules.
  function teamDescription(pages) {
    const names = pages.slice(0, TEAM_DESC_MAX).map((p) => p.label);
    if (pages.length > TEAM_DESC_MAX) names.push(`+${pages.length - TEAM_DESC_MAX} more`);
    return names.join(" · ");
  }

  return (
    <div className="home-page">
      <section className="home-welcome">
        <p className="home-welcome-eyebrow">Welcome back,</p>
        <h1 className="home-welcome-name">
          {name || "there"}
        </h1>
        <p className="home-welcome-date">{formatDate()}</p>
        <p className="home-welcome-sub">
          Select a team to continue to the respective modules.
        </p>
      </section>

      <section className="home-section">
        <div className="home-section-head">
          <h2 className="home-section-title">Your Teams</h2>
          <p className="home-section-sub">
            You have access to the following teams. Click a team to explore its
            modules.
          </p>
        </div>
        {teams.length === 0 ? (
          <p className="home-empty">You are not allocated to any team yet.</p>
        ) : (
          <div className="home-teams-grid">
            {teams.map(({ division, pages, lead, leadTo }) => (
              <Link
                key={division}
                to={leadTo}
                className="home-team-card"
                style={accentVars(DIVISION_ACCENT[division] || DEFAULT_ACCENT)}
              >
                <span className="home-team-card-top">
                  <span className="home-card-icon home-team-icon" aria-hidden="true">
                    <NavIcon type={ICON_ALIASES[lead.icon] || lead.icon} />
                  </span>
                  <span className="home-card-arrow" aria-hidden="true">
                    ›
                  </span>
                </span>
                <span className="home-team-name">{division}</span>
                <span className="home-team-modules">
                  {pages.length} {pages.length === 1 ? "module" : "modules"}
                </span>
                <span className="home-team-desc">{teamDescription(pages)}</span>
              </Link>
            ))}
          </div>
        )}
      </section>

      {homeModules.length > 0 && (
        <section className="home-section">
          <div className="home-section-head">
            <h2 className="home-section-title">Support</h2>
            <p className="home-section-sub">
              Modules available across the portal, outside any team.
            </p>
          </div>
          <div className="home-teams-grid">
            {homeModules.map((page) => (
              <Link
                key={page.pageKey}
                to={page.route}
                className="home-team-card"
                style={accentVars(DIVISION_ACCENT[HOME_DIVISION] || DEFAULT_ACCENT)}
              >
                <span className="home-team-card-top">
                  <span className="home-card-icon home-team-icon" aria-hidden="true">
                    <NavIcon type={ICON_ALIASES[page.icon] || page.icon} />
                  </span>
                  <span className="home-card-arrow" aria-hidden="true">
                    ›
                  </span>
                </span>
                <span className="home-team-name">{page.label}</span>
                <span className="home-team-modules">1 module</span>
                <span className="home-team-desc">
                  {(page.actions || [])
                    .filter((a) => mayPerform(page.pageKey, a.action))
                    .map((a) => a.label)
                    .join(" · ") || "Open"}
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="home-section">
        <h2 className="home-section-title">My Actions</h2>
        {!actionsReady ? (
          <p className="home-empty">Checking for pending items…</p>
        ) : actions.length === 0 ? (
          <p className="home-empty">
            You're all caught up — nothing is waiting on you right now.
          </p>
        ) : (
          <div className="home-actions-grid">
            {actions.map((action) => (
              <Link
                key={action.id}
                to={action.route}
                className="home-action-card"
                style={accentVars(ACTION_ACCENT[action.id] || DEFAULT_ACCENT)}
              >
                <span className="home-action-head">
                  <span className="home-card-icon" aria-hidden="true">
                    <NavIcon type={action.icon} />
                  </span>
                  <span className="home-action-label">{action.label}</span>
                  <span
                    className="home-action-count"
                    aria-label={`${action.count} pending`}
                  >
                    {action.count}
                  </span>
                </span>
                <span className="home-action-note">{action.note}</span>
                <span className="home-action-view">View</span>
              </Link>
            ))}
          </div>
        )}
      </section>

      {recentPages.length > 0 && (
        <section className="home-section">
          <h2 className="home-section-title">Recently Used</h2>
          <ul className="home-recent-list">
            {recentPages.map((entry) => (
              <li key={entry.route}>
                <Link
                  to={entry.route}
                  className="home-recent-chip"
                  style={accentVars(
                    DIVISION_ACCENT[entry.page.division] || DEFAULT_ACCENT
                  )}
                >
                  <span className="home-recent-icon" aria-hidden="true">
                    <NavIcon
                      type={ICON_ALIASES[entry.page.icon] || entry.page.icon}
                    />
                  </span>
                  <span className="home-recent-label">{entry.page.label}</span>
                  <span className="home-recent-time">
                    {formatWhen(entry.visitedAt)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
